import React, { useState, ChangeEvent, useRef, useEffect, FormEvent } from 'react';
import './Components.css';
import PlusSign from './PlusSign';
import CroppingModal from './CroppingModal';
import WhiteX from './WhiteX';
import heic2any from "heic2any";
import { readAndCompressImage } from 'browser-image-resizer';
import ReCAPTCHA from 'react-google-recaptcha';
import LoadingSpinner from './LoadingSpinner';

const config = {
    quality: 0.5,
    maxWidth: 800,
    maxHeight: 1000,
    debug: true
  };

type AuthlessPhotoUploadProps = {
    sModalOpen: boolean;
    setSModalOpen: (value: boolean) => void;
    numPhotos: number;
    setNumPhotos: (value: number) => void;
    setPhotoIds: React.Dispatch<React.SetStateAction<Array<string>>>;
}

const AuthlessPhotoUpload: React.FC<AuthlessPhotoUploadProps> = ({ sModalOpen, setSModalOpen, numPhotos, setNumPhotos, setPhotoIds }) => {
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [tmpImage, setTmpImage] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [photoId, setPhotoId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const recaptchaRef = React.createRef<ReCAPTCHA>();
    const [isUploading, setIsUploading] = useState(false);

    const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
        setIsUploading(true);
        const inputf = event.target.files?.[0];
        if (!inputf) return;
        const file = await readAndCompressImage(inputf, config);
        if (file) {
            let imageUrl = URL.createObjectURL(file);
            if (file.type === 'image/heic') {
                try {
                    const blob = await fetch(imageUrl).then((res) => res.blob());
                    const conversionResult = await heic2any({ blob, toType: "image/jpeg", quality: 0.5 });

                    if (conversionResult instanceof Blob) {
                        imageUrl = URL.createObjectURL(conversionResult);
                    } else {
                        setIsUploading(false);
                        console.error('HEIC conversion returned unexpected result.');
                    }
                } catch (error) {
                    setIsUploading(false);
                    console.error('HEIC conversion error:', error);
                    return;
                }
            }
            setTmpImage(imageUrl);
            setModalOpen(true);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const handleComponentClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleConfirmCrop = (croppedImage: string) => {
        setSelectedImage(croppedImage);
        closeModal();
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setTmpImage(null);
        setNumPhotos(numPhotos-1);
        if (photoId) {
            setPhotoIds(prevPhotoIds => prevPhotoIds.filter(id => id !== photoId));
        }
    };

    useEffect(() => {
        setSModalOpen(modalOpen);
    }, [modalOpen]);

    useEffect(() => {
        if (recaptchaRef && recaptchaRef.current) {
            recaptchaRef.current.execute();
        }
    }, [selectedImage]);

    useEffect(() => {
        if (selectedImage && recaptchaToken) {
            const formdata = new FormData();
            const filename = "default.jpeg";

            const ogNumPhotos = numPhotos;
            setNumPhotos(numPhotos+1); // before we were updating photoIds and numPhotos non-atomically which caused the issue of continue being active but photoIds being []

            fetch(selectedImage).then(res => res.blob()).then(blob => {
                formdata.append("photo", blob, filename);
                formdata.append("captcha", recaptchaToken);
                const requestOptions: RequestInit = {
                    method: 'POST',
                    body: formdata,
                    redirect: 'follow'
                };
    
                fetch("https://api.ishemine.com/evals/leader/index/", requestOptions)
                .then(response => response.text())
                .then(resp => {
                    console.log("response from indexing", resp);
                    const parsed = JSON.parse(resp);
                    console.log(parsed["id"]);
                    setPhotoId(parsed["id"]);
                    setPhotoIds(prevPhotoIds => [...prevPhotoIds, parsed["id"]]);
                    setIsUploading(false);
                })
                .catch(error => {
                    console.log('error in indexing', error);
                    setNumPhotos(ogNumPhotos);
                    setIsUploading(false);
                    // TODO: show error message, remove photo from card
                });
            });
        }
    }, [recaptchaToken]);

    useEffect(() => {
        console.log(`isUploading: ${isUploading}`);
    }, [isUploading]);

    return (
        <>
            {
                sModalOpen
                ?   <div className="w-24 h-32 md:w-28 md:h-36 pt-3 relative photo-upload-container" onClick={(!selectedImage) ? handleComponentClick : () => null}>
                        {
                            selectedImage
                            ? <div 
                                className="photo-upload-bg w-20 h-28 md:w-24 md:h-32 rounded-lg shadow-md"
                                style={{
                                    backgroundImage: `url(${selectedImage})`,
                                    backgroundSize: 'cover',
                                }}
                            ></div>
                            : <div 
                                className="photo-upload-bg w-20 h-28 md:w-24 md:h-32 rounded-lg border border-dashed border-black border-2"
                            ></div>
                        }
                        <div className="photo-upload-plus-bg text-white font-bold text-3xl w-8 h-8 rounded-full flex items-center justify-center absolute top-0 right-0" onClick={(selectedImage) ? handleRemoveImage : () => null}>
                            {
                                selectedImage
                                ? <WhiteX/>
                                : <PlusSign/>
                            }
                        </div>
                        <CroppingModal
                            isOpen={modalOpen}
                            closeModal={closeModal}
                            image={tmpImage}
                            onCropConfirm={handleConfirmCrop}
                        />
                    </div>
                :   <div className="w-24 h-32 md:w-28 md:h-36 pt-3 relative photo-upload-container hover:cursor-pointer" onClick={(!selectedImage) ? handleComponentClick : () => null}>
                        {
                            selectedImage
                            ? <div 
                                className="photo-upload-bg relative w-20 h-28 md:w-24 md:h-32 rounded-lg shadow-md"
                                style={{
                                    backgroundImage: `url(${selectedImage})`,
                                    backgroundSize: 'cover',
                                }}
                            >
                                {
                                    isUploading && (
                                        <div className="absolute w-full h-full rounded-lg inset-0 bg-black bg-opacity-30 flex justify-center items-center">
                                            <LoadingSpinner />
                                        </div>
                                    )
                                }
                            </div>
                            : <div 
                                className="photo-upload-bg relative w-20 h-28 md:w-24 md:h-32 rounded-lg border border-dashed border-black border-2"
                            >
                                {
                                    isUploading && (
                                        <div className="absolute w-full h-full rounded-lg inset-0 bg-black bg-opacity-30 flex justify-center items-center">
                                            <LoadingSpinner />
                                        </div>
                                    )
                                }
                            </div>
                        }
                        <div className="photo-upload-plus-bg text-white font-bold text-3xl w-8 h-8 rounded-full flex items-center justify-center absolute top-0 right-0" onClick={(selectedImage) ? handleRemoveImage : () => null}>
                            {
                                selectedImage
                                ? <WhiteX/>
                                : <PlusSign/>
                            }
                        </div>
                        <input
                            type="file"
                            accept="image/jpeg, image/png"
                            onChange={handleImageUpload}
                            className="hidden"
                            ref={fileInputRef}
                        />
                        <CroppingModal
                            isOpen={modalOpen}
                            closeModal={closeModal}
                            image={tmpImage}
                            onCropConfirm={handleConfirmCrop}
                        />
                    </div>
            }
            <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey="6LcdznYnAAAAAFicIrLfu9EJuODfhkIScxm1_nCS"
                    onChange={(token: string | null) => token ? setRecaptchaToken(token) : null}
                    />
        </>
    );
};

export default AuthlessPhotoUpload;